import { StickyHeader } from '../../../components/sticky-header/sticky-header';
import { Button } from '../../../components/form/button';
import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { OrderSection } from '../../order/order-section/order-section';
import { VendorOrderSelect } from '../../vendor/vendor-order-select';
import { Vendor } from '../../../clients/quipli-service/vendor/vendor';
import { LocationSelect } from '../../location/select/location-select';
import { Location } from '../../../clients/quipli-service/location/location';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { DateTime } from 'luxon';
import { PurchaseLineItemForm } from '../components/purchase-line-item-form';
import { PurchaseOrderLineItems } from '../purchase-order-line-items';
import {
  PurchaseOrderPaymentMethod,
  PurchaseOrderProductLineItem,
  toUpsertPurchaseOrderProductRequest,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { PurchasePaymentSelect } from '../purchase-payment-select';
import { toast } from '../../../util/toasts';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { useNavigate } from 'react-router-dom';
import { TenantContext } from '../../../context/tenant-context';
import { Currency } from 'dinero.js';
import { PurchaseOrderDetails } from '../purchase-order-details/purchase-order-details';
import { useVendorApi } from '../../../clients/quipli-service/vendor/vendor-api';

export const CreatePurchaseOrder = () => {
  const { createPurchaseOrder } = usePurchaseOrderApiV1();
  const { getVendor } = useVendorApi();
  const { contextPath } = useContext(TenantContext);
  const navigate = useNavigate();

  const [location, setLocation] = useState<Location>();
  const [vendor, setVendor] = useState<Vendor>();
  const [admin, setAdmin] = useState<Admin>();
  const [date, setDate] = useState<DateTime>();
  const [paymentMethod, setPaymentMethod] = useState<PurchaseOrderPaymentMethod>();
  const [items, setItems] = useState<PurchaseOrderProductLineItem[]>([]);

  useEffect(() => {
    if (!vendor) {
      const defaultVendorId = items.find((i) => i.product.defaultVendorId)?.product.defaultVendorId;
      if (defaultVendorId) {
        getVendor(defaultVendorId)
          .then(setVendor)
          .catch((e: any) => {
            console.error('Failed to assign default vendor', e);
          });
      }
    }
  }, [items, vendor]);

  const submit = async () => {
    if (!location) {
      toast('Please select a location.', 'error');
      return;
    }
    if (!vendor) {
      toast('Please select a vendor.', 'error');
      return;
    }
    if (!admin) {
      toast('Please select who purchased the order.', 'error');
      return;
    }
    if (!date) {
      toast('Please select a date.', 'error');
      return;
    }
    if (items.length === 0) {
      toast('Please add items to the order.', 'error');
      return;
    }
    // Submit the order
    try {
      const po = await createPurchaseOrder({
        locationId: location.id,
        vendorId: vendor.id,
        purchasedById: admin.adminId,
        paymentMethodId: paymentMethod?.id,
        purchasedAt: date,
        products: items.map(toUpsertPurchaseOrderProductRequest),
      });
      toast('Purchase order created successfully.', 'success');
      // Redirect to the purchase order view
      navigate(`${contextPath}manager/purchase-orders/${po.id}`);
    } catch (e) {
      toast('Failed to create purchase order.', 'error');
    }
  };

  useEffect(() => {
    if (!date && location) setDate(DateTime.local({ zone: location.timezone }));
  }, [location]);

  return (
    <div className="px-[10px]">
      <StickyHeader title="Purchase Order">
        <Button className="button-yellow" onClick={submit} dataCy="create-purchase-order">
          Create
        </Button>
      </StickyHeader>

      <div className={classNames('grid grid-cols-1 lg:grid-cols-3 gap-y-[15px] lg:gap-y-0 lg:gap-x-[15px] mt-[15px]')}>
        <div className="col-span-2 order-2 lg:order-1 flex flex-col gap-y-[15px]">
          <OrderSection className="flex flex-col gap-[12px]" header="Purchase Location">
            <LocationSelect setLocation={(l) => setLocation(l || undefined)} dataCy="location-select" />
          </OrderSection>

          <PurchaseLineItemForm location={location} onAddItem={(item) => setItems((prev) => [...prev, item])} />

          <PurchaseOrderLineItems
            canUpdateOrder={false}
            productLineItems={items}
            setProductLineItems={setItems}
            currency={(location?.currency || 'USD') as Currency}
          />

          <PurchasePaymentSelect isEditing selectedPaymentMethod={paymentMethod} onSelect={setPaymentMethod} />
        </div>

        <div className="order-1 lg:order-2 flex flex-col gap-y-[15px]">
          <VendorOrderSelect vendor={vendor} setVendor={setVendor} isEditMode={true} />

          <PurchaseOrderDetails
            isEditing
            date={date}
            purchasedBy={admin}
            setDate={setDate}
            setPurchasedBy={setAdmin}
            useSignedAdminAsDefault
          />
        </div>
      </div>
    </div>
  );
};
